'use client';

import { useEffect, useState } from 'react';
import { fetchAPI } from '@/core/api/client';

/** What ING Lease Now's availability check returns (backend /payments/leasenow/check-availability). */
export interface LeaseNowAvailability {
    availability?: boolean;
    currencyIsoName?: string;
    missingNetAmount?: number;
    redirectUrl?: string;
    imageUrl?: string;
    additionalMessage?: { languageCode: string; message: string }[];
    products?: { availability?: boolean; monthlyPaymentNet?: number }[];
    monthlyPaymentNet?: number;
    [key: string]: any;
}

export interface LeaseNowSimulationParams {
    leasingPeriodMonths: number;
    ownContributionPercent: number;
    residualValuePercent: number;
    manufacturerName?: string | null;
}

// The product page can show the leasing button, the calculator and the old badge at
// once — they share one request per product/variant/quantity instead of each asking LeaseNow.
const CACHE_TTL_MS = 60_000;
const cache = new Map<string, { at: number; promise: Promise<LeaseNowAvailability | null> }>();

function roundTwo(value: number) {
    return Math.round(value * 100) / 100;
}

function grossPrice(product: any, variant: any): number {
    const source = variant || product;
    const price = source?.sales_price !== null && source?.sales_price !== undefined ? source.sales_price : source?.price;
    return Number(price);
}

function taxRateOf(product: any): number {
    const raw = product?.tax_rate ?? product?.taxRate;
    if (typeof raw === 'number') return raw;
    return parseFloat(raw || '23') || 23;
}

export function leaseNowNetPrice(product: any, variant?: any): number {
    const gross = grossPrice(product, variant);
    return !isNaN(gross) && gross > 0 ? roundTwo(gross / (1 + taxRateOf(product) / 100)) : 0;
}

function checkAvailabilityPayload(body: Record<string, any>): Promise<LeaseNowAvailability | null> {
    const key = JSON.stringify(body);
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;
    const promise = fetchAPI('/payments/leasenow/check-availability', { method: 'POST', body: JSON.stringify(body) })
        .then((res: any) => (res && typeof res === 'object' ? res as LeaseNowAvailability : null))
        .catch(() => null);
    cache.set(key, { at: Date.now(), promise });
    return promise;
}

function checkAvailability(product: any, variant: any, quantity: number, valueNet: number, leasingParams?: LeaseNowSimulationParams): Promise<LeaseNowAvailability | null> {
    const productManufacturer = product.brand?.name || product.brand_name || product.manufacturer_name;
    const manufacturerName = leasingParams?.manufacturerName?.trim()
        || (typeof productManufacturer === 'string' ? productManufacturer.trim() : '')
        || null;
    const normalizedLeasingParams = leasingParams
        ? { ...leasingParams, manufacturerName }
        : undefined;

    return checkAvailabilityPayload({
        products: [{
            id: variant ? `${product.id}-${variant.id}` : `${product.id}`,
            name: product.name,
            quantity,
            valueNet,
            valueVatPercent: taxRateOf(product),
            url: typeof window !== 'undefined' ? window.location.href.split('?')[0] : '',
            state: 'NEW',
            categoryId: product.categories?.[0]?.name || product.category_name || product.all_categories?.[0]?.name || 'Sprzęt',
            ...(normalizedLeasingParams ? { leasingParams: normalizedLeasingParams } : {}),
        }],
        currencyIsoName: 'PLN',
    });
}

/**
 * LeaseNow availability and optional monthly-payment simulation for one product line. Does nothing — no
 * request — when `enabled` is false, e.g. the integration is switched off.
 */
export function useLeaseNowAvailability(product: any, variant: any, quantity: number, enabled: boolean, leasingParams?: LeaseNowSimulationParams) {
    const [data, setData] = useState<LeaseNowAvailability | null>(null);
    const [loading, setLoading] = useState(false);
    const valueNet = product ? leaseNowNetPrice(product, variant) : 0;
    const qty = Math.max(1, quantity || 1);
    const paramsKey = leasingParams
        ? `${leasingParams.leasingPeriodMonths}-${leasingParams.ownContributionPercent}-${leasingParams.residualValuePercent}-${leasingParams.manufacturerName || ''}`
        : '';

    useEffect(() => {
        if (!enabled || !product?.id || valueNet <= 0) {
            setData(null);
            setLoading(false);
            return;
        }
        let active = true;
        setLoading(true);
        checkAvailability(product, variant, qty, valueNet, leasingParams).then((res) => {
            if (!active) return;
            setData(res);
            setLoading(false);
        });
        return () => { active = false; };
        // product/variant objects change identity on re-render; their ids + price + quantity are what matter
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, product?.id, variant?.id, valueNet, qty, paramsKey]);

    const line = data?.products?.[0];
    return {
        data,
        loading,
        /** LeaseNow finances this cart line: eligible product AND the cart minimum is reached. */
        available: !!data?.availability && line?.availability !== false,
        monthlyPaymentNet: typeof line?.monthlyPaymentNet === 'number' ? line.monthlyPaymentNet : null,
    };
}

function readCart(): any[] {
    if (typeof window === 'undefined') return [];
    try {
        const value = JSON.parse(window.localStorage.getItem('stag-cart') || '[]');
        return Array.isArray(value) ? value : [];
    } catch {
        return [];
    }
}

/**
 * LeaseNow availability and optional combined monthly-payment simulation for the current cart. The hook is used by
 * standalone page-builder blocks, so the button and calculator can be placed independently of
 * the main cart block.
 */
export function useLeaseNowCartAvailability(enabled: boolean, leasingParams?: LeaseNowSimulationParams) {
    const [cart, setCart] = useState<any[]>([]);
    const [data, setData] = useState<LeaseNowAvailability | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const sync = () => setCart(readCart());
        sync();
        window.addEventListener('cart-updated', sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener('cart-updated', sync);
            window.removeEventListener('storage', sync);
        };
    }, []);

    const cartKey = cart
        .map((item: any) => `${item.id}-${item.variant_id || 'b'}-${item.price}-${item.quantity || 1}`)
        .join('|');
    const paramsKey = leasingParams
        ? `${leasingParams.leasingPeriodMonths}-${leasingParams.ownContributionPercent}-${leasingParams.residualValuePercent}-${leasingParams.manufacturerName || ''}`
        : '';

    useEffect(() => {
        if (!enabled || cart.length === 0) {
            setData(null);
            setLoading(false);
            return;
        }

        const products = cart.map((item: any) => {
            const priceGross = Number(item.price) || 0;
            const rawTaxRate = item.tax_rate ?? item.taxRate ?? 23;
            const taxRate = typeof rawTaxRate === 'number' ? rawTaxRate : (parseFloat(rawTaxRate) || 23);
            const manufacturerName = leasingParams?.manufacturerName?.trim()
                || item.brand?.name
                || item.brand_name
                || item.manufacturer_name
                || null;
            return {
                id: item.variant_id ? `${item.id}-${item.variant_id}` : `${item.id}`,
                name: item.name,
                quantity: Math.max(1, Number(item.quantity) || 1),
                valueNet: roundTwo(priceGross / (1 + taxRate / 100)),
                valueVatPercent: taxRate,
                url: typeof window !== 'undefined' ? `${window.location.origin}/produkty/${item.slug || ''}` : '',
                state: 'NEW',
                categoryId: item.category_name || 'Sprzęt',
                ...(leasingParams ? { leasingParams: { ...leasingParams, manufacturerName } } : {}),
            };
        });

        let active = true;
        setLoading(true);
        checkAvailabilityPayload({ products, currencyIsoName: 'PLN' }).then((res) => {
            if (!active) return;
            setData(res);
            setLoading(false);
        });
        return () => { active = false; };
        // cartKey represents every field used to build the availability payload.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, cartKey, paramsKey]);

    const productRates = (data?.products || [])
        .map((line) => line.monthlyPaymentNet)
        .filter((rate): rate is number => typeof rate === 'number');
    const monthlyPaymentNet = typeof data?.monthlyPaymentNet === 'number'
        ? data.monthlyPaymentNet
        : productRates.length > 0
            ? roundTwo(productRates.reduce((sum, rate) => sum + rate, 0))
            : null;

    return {
        cart,
        data,
        loading,
        available: !!data?.availability,
        monthlyPaymentNet,
    };
}
